import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import LandingPage from "@/pages/LandingPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminCollection from "@/pages/admin/AdminCollection";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminApiKeys from "@/pages/admin/AdminApiKeys";

// ---------- Social Operator (vendored from github.com/Tylegn/TySite) ----------
import { ScopeProvider } from "@/context/ScopeContext";
import { AppShell } from "@/layout/AppShell";
import Dashboard from "@/pages/admin/social/Dashboard";
import Brands from "@/pages/admin/social/Brands";
import BrandDetail from "@/pages/admin/social/BrandDetail";
import Accounts from "@/pages/admin/social/Accounts";
import Composer from "@/pages/admin/social/Composer";
import Drafts from "@/pages/admin/social/Drafts";
import CalendarPage from "@/pages/admin/social/CalendarPage";
import CommentsPage from "@/pages/admin/social/CommentsPage";
import InboxPage from "@/pages/admin/social/InboxPage";
import Analytics from "@/pages/admin/social/Analytics";
import Automation from "@/pages/admin/social/Automation";
import AuditLogs from "@/pages/admin/social/AuditLogs";
import Settings from "@/pages/admin/social/Settings";

import { Toaster } from "@/components/ui/sonner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1, staleTime: 30_000 },
  },
});

// Renders the TySoce-style operator shell (sidebar + topbar + grain) around
// the active nested social route. Wraps with ScopeProvider so all child
// pages have access to brands/accounts/emergency state.
function SocialShell() {
  // The TySoce CSS tokens expect the `.dark` class on documentElement; toggle
  // it on while a social page is mounted, then revert on unmount so the
  // public landing page keeps its own theming.
  useEffect(() => {
    const had = document.documentElement.classList.contains("dark");
    document.documentElement.classList.add("dark");
    return () => {
      if (!had) document.documentElement.classList.remove("dark");
    };
  }, []);

  return (
    <ScopeProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </ScopeProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* Existing admin (Profile, Companies, Featured, Portfolio, Experience, Messages) */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/profile" replace />} />
              <Route path="profile" element={<AdminProfile />} />
              <Route path="companies" element={<AdminCollection slug="companies" />} />
              <Route path="featured" element={<AdminCollection slug="featured_projects" />} />
              <Route path="portfolio" element={<AdminCollection slug="portfolio" />} />
              <Route path="experience" element={<AdminCollection slug="experience" />} />
              <Route path="messages" element={<AdminMessages />} />
              <Route path="keys" element={<AdminApiKeys scope="admin" />} />
            </Route>

            {/* Social Operator (vendored TySoce) — protected behind the same admin JWT */}
            <Route
              path="/admin/social"
              element={
                <ProtectedRoute>
                  <SocialShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="brands" element={<Brands />} />
              <Route path="brands/:id" element={<BrandDetail />} />
              <Route path="accounts" element={<Accounts />} />
              <Route path="composer" element={<Composer />} />
              <Route path="drafts" element={<Drafts />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="comments" element={<CommentsPage />} />
              <Route path="inbox" element={<InboxPage />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="automation" element={<Automation />} />
              <Route path="audit" element={<AuditLogs />} />
              <Route path="keys" element={<AdminApiKeys scope="social" />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
